import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { adminNav, partnerNav, comingSoonItems } from "@/data/modules";
import Login from "@/pages/Login";
import Dashboard from "@/pages/admin/Dashboard";
import Exchanges from "@/pages/admin/Exchanges";
import AddExchange from "@/pages/admin/AddExchange";
import ExchangeDetail from "@/pages/admin/ExchangeDetail";
import Portfolio from "@/pages/admin/Portfolio";
import StatsReports from "@/pages/admin/StatsReports";
import ComingSoon from "@/pages/ComingSoon";
import MyClients from "@/pages/partner/MyClients";
import ClientManage from "@/pages/partner/ClientManage";
import PartnerPortfolio from "@/pages/partner/PartnerPortfolio";

const PARTNER_HOME = "/partner/clients";

function FullLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
      <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" />
    </div>
  );
}

const homeFor = (role) => (role === "ADMIN" ? "/dashboard" : PARTNER_HOME);

function RequireRole({ role, children }) {
  const { user } = useAuth();
  if (user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== role) return <Navigate to={homeFor(user.role)} replace />;
  return children;
}

function RootRedirect() {
  const { user } = useAuth();
  if (user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={homeFor(user.role)} replace />;
}

function LoginRoute() {
  const { user } = useAuth();
  if (user === null) return <FullLoader />;
  if (user) return <Navigate to={homeFor(user.role)} replace />;
  return <Login />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<LoginRoute />} />

            {/* ADMIN */}
            <Route element={<RequireRole role="ADMIN"><AppShell nav={adminNav} role="ADMIN" notifications={49} /></RequireRole>}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/exchanges" element={<Exchanges />} />
              <Route path="/exchanges/new" element={<AddExchange />} />
              <Route path="/exchanges/:id" element={<ExchangeDetail />} />
              <Route path="/portfolio" element={<Portfolio />} />
              <Route path="/portfolio/:id" element={<StatsReports />} />
              {comingSoonItems(adminNav).map((m) => (
                <Route key={m.path} path={m.path} element={<ComingSoon module={m} />} />
              ))}
            </Route>

            {/* CHANNEL PARTNER */}
            <Route element={<RequireRole role="CHANNEL_PARTNER"><AppShell nav={partnerNav} role="CHANNEL_PARTNER" notifications={3} /></RequireRole>}>
              <Route path="/partner" element={<Navigate to={PARTNER_HOME} replace />} />
              <Route path="/partner/clients" element={<MyClients />} />
              <Route path="/partner/clients/:id" element={<ClientManage />} />
              <Route path="/partner/portfolio" element={<PartnerPortfolio />} />
              <Route path="/partner/portfolio/:id" element={<StatsReports base="/partner/portfolio" backPath="/partner/portfolio" partner />} />
              {comingSoonItems(partnerNav).map((m) => (
                <Route key={m.path} path={m.path} element={<ComingSoon module={m} />} />
              ))}
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" theme="dark" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
